import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Checkbox } from 'components/v2/Checkbox'
import { CopyToClipboard, CopyToClipboardWrapper } from '@10xLabs/react-web/components/CopyToClipboard'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { Table, Thead, Th, Tbody, Tr, Td } from '@10xLabs/react-web/components/Table'
import { InputCurrency } from 'components/InputCurrency'
import { paymentMethodMessages } from 'features/Bookings/components/PaymentMethod/messages'
import { safeFormatMessage } from 'utils/intl'
import { sanitizePrice } from 'utils/math'
import {
  GTBookingV2,
  GTBookingV2Payment,
  GTBookingV2PaymentMethod,
  GTBookingV2PaymentProcessor
} from 'gql/types.generated'
import { ReactElement } from 'react'

interface IPaymentMethodProps {
  payment: GTBookingV2Payment
}

const PaymentMethod = ({ payment }: IPaymentMethodProps): ReactElement => {
  const intl = useIntl()
  switch (payment.method) {
    case GTBookingV2PaymentMethod.Kredit:
      return <FormattedMessage id="dictionary.credit" defaultMessage="Kredits" />
    case GTBookingV2PaymentMethod.Cash:
      return <FormattedMessage id="dictionary.cash" defaultMessage="Cash" />
    case GTBookingV2PaymentMethod.Coupon:
      return <FormattedMessage id="dictionary.coupon" defaultMessage="Coupon" />
  }

  if (payment.processor === GTBookingV2PaymentProcessor.Conekta) {
    return (
      <CopyToClipboardWrapper>
        <div>{payment.chargeID?.substring(0, 8)}</div>
        <CopyToClipboard str={payment.chargeID} />
      </CopyToClipboardWrapper>
    )
  }

  return <>{`${safeFormatMessage({ intl, messages: paymentMethodMessages, key: payment.method })} ${payment.last4}`}</>
}

const InputTd = styled(Td)<TSFixMe>`
  input {
    padding: 7px 9px;
    width: calc(100% - 20px);
    min-width: ${(props): TSFixMe => (props.largeInput ? '84px' : '50px')};
    font-size: 14px;
    max-width: 100px;
  }
`

const H5 = styled.h5<TSFixMe>`
  margin: 20px 0 0;
  font-size: 14px;
  text-transform: uppercase;
`

const TotalsLabel = styled.div<TSFixMe>`
  font-weight: 500;
`

interface ISelectPaymentsProps {
  booking: GTBookingV2
  paidPayments: GTBookingV2Payment[]
  formProps: TSFixMe
}

export const SelectPayments = ({ booking, paidPayments, formProps }: ISelectPaymentsProps): ReactElement => {
  const disableInput = paidPayments.length === 1
  const totalPaidAmount = paidPayments.reduce((total: TSFixMe, payment: TSFixMe): TSFixMe => {
    return total + payment.amount
  }, 0)
  const totalAmountToRefund = formProps.values.selectedPayments.reduce(
    (total: TSFixMe, selectedPayment: TSFixMe): TSFixMe => {
      return total + parseFloat(selectedPayment.amountToRefund)
    },
    0
  )

  const pricesCurrency = booking.currency.code === 'USD' ? 'USD' : undefined

  return (
    <>
      <H5>
        <FormattedMessage id="booking.cancellation.partial.payments.title" defaultMessage="Pagos" />
      </H5>
      <Table>
        <Thead>
          <Tr>
            <Th />
            <Th>
              <FormattedMessage id="booking.cancellation.partial.payments.method" defaultMessage="Payment method" />
            </Th>
            <Th>
              <FormattedMessage id="booking.cancellation.partial.payments.amount" defaultMessage="Amount" />
            </Th>
            <Th>
              <FormattedMessage
                id="booking.cancellation.partial.payments.amountToRefund"
                defaultMessage="Amount to refund"
              />
            </Th>
            <Th>
              <FormattedMessage id="booking.cancellation.partial.payments.leftover" defaultMessage="Leftover" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {paidPayments.map((payment: TSFixMe): TSFixMe => {
            const handleChange = ({ target: { checked } }: TSFixMe): TSFixMe => {
              if (checked) {
                formProps.setFieldValue('selectedPayments', [
                  ...formProps.values.selectedPayments,
                  {
                    processor: payment.processor,
                    paymentId: payment.id,
                    amountToRefund: '',
                    paidAmount: payment.amount
                  }
                ])
              } else {
                formProps.setFieldValue(
                  'selectedPayments',
                  formProps.values.selectedPayments.filter(
                    ({ paymentId }: TSFixMe): TSFixMe => paymentId !== payment.id
                  )
                )
              }
            }

            const selectedPaymentIndex = formProps.values.selectedPayments.findIndex(
              ({ paymentId }: TSFixMe): TSFixMe => paymentId === payment.id
            )
            const hasSelectedPayment = selectedPaymentIndex > -1
            let selectedPayment
            let leftover = payment.amount
            if (hasSelectedPayment) {
              selectedPayment = formProps.values.selectedPayments[selectedPaymentIndex]
              leftover = sanitizePrice(payment.amount - selectedPayment.amountToRefund * 100)
            }

            return (
              <Tr key={payment.id}>
                <Td>
                  <Checkbox
                    id="paymentsToRefund.id"
                    checked={selectedPaymentIndex > -1}
                    onChange={handleChange}
                    disabled={disableInput}
                    readOnly={disableInput}
                  />
                </Td>
                <Td>
                  <PaymentMethod payment={payment} />
                </Td>
                <Td>
                  <FormattedPrice value={payment.amount} currency={pricesCurrency} />
                </Td>
                <InputTd>
                  {hasSelectedPayment ? (
                    <InputCurrency
                      id={`selectedPayments[${selectedPaymentIndex}].amountToRefund`}
                      value={selectedPayment.amountToRefund}
                      placeholder="$0.00"
                      onChange={(value): TSFixMe => {
                        formProps.setFieldValue(`selectedPayments[${selectedPaymentIndex}].amountToRefund`, value)
                      }}
                      onBlur={formProps.handleBlur}
                      touched={
                        formProps.touched.selectedPayments &&
                        formProps.touched.selectedPayments[selectedPaymentIndex] &&
                        formProps.touched.selectedPayments[selectedPaymentIndex].amountToRefund
                      }
                      errors={
                        formProps.errors.selectedPayments &&
                        formProps.errors.selectedPayments[selectedPaymentIndex] &&
                        formProps.errors.selectedPayments[selectedPaymentIndex].amountToRefund
                      }
                      suffix={pricesCurrency ? ' USD' : ''}
                      disabled={disableInput}
                      readOnly={disableInput}
                    />
                  ) : (
                    '-'
                  )}
                </InputTd>
                <Td>
                  <FormattedPrice value={leftover} currency={pricesCurrency} />
                </Td>
              </Tr>
            )
          })}
        </Tbody>
        <tfoot>
          <Tr>
            <td />
            <Td>
              <TotalsLabel>
                <FormattedMessage
                  id="booking.cancellation.partial.payments.footer.totals.label"
                  defaultMessage="Totals"
                />
              </TotalsLabel>
            </Td>
            <Td>
              <FormattedPrice value={totalPaidAmount} currency={pricesCurrency} />
            </Td>
            <Td>
              <FormattedPrice value={sanitizePrice(totalAmountToRefund * 100)} currency={pricesCurrency} />
            </Td>
            <Td>
              <FormattedPrice
                value={sanitizePrice(totalPaidAmount - totalAmountToRefund * 100)}
                currency={pricesCurrency}
              />
            </Td>
          </Tr>
        </tfoot>
      </Table>
    </>
  )
}
